import { Guid } from '../../../application/store/model/guid';
import { LinkPosition } from '../../../graphql/__generated/graphql';
import { KitIcon, KitIconHelper } from './KitIcon';
import { LinkPositionHelper } from './LinkPosition';
import { SceneGuid } from './Scene';
import {
  HandleSceneLinkClick,
  ImageHotspot,
  Position,
  SceneLinkHotspot,
  SceneLinkHotspotParameters,
} from '@ael/core-api';
import uuid from 'uuid';
import {VirtualTourIconsBundle} from "@ael/virtual-tour-icon-bundle";
import backgroundUrlTilted from "../../../resources/wrapper-tilted.svg";
import backgroundUrl from "../../../resources/wrapper.svg";
import imageUrl from "../../../resources/arrow-tilted.svg";

export type SceneLink = Readonly<{
  guid: SceneLinkGuid;
  originTourSceneGuid: SceneGuid;
  destinationTourSceneGuid: SceneGuid;
  position: LinkPosition;
  icon: KitIcon;
  destinationLabel?: string;
  hasTilt: boolean;
}>;

export type SceneLinkGuid = Guid & {
  __brand: 'sceneLinkGuid';
};

export const SceneLinkHelper = {
  createGuid: (): SceneLinkGuid => uuid() as SceneLinkGuid,
  create: (
    position: Position,
    destinationTourSceneGuid: SceneGuid,
    guid?: SceneLinkGuid,
    iconData?: KitIcon,
    destinationLabel?: string,
    hasTilt?: boolean
  ): SceneLink => ({
    guid: guid ?? SceneLinkHelper.createGuid(),
    icon: iconData ? KitIconHelper.create(iconData) : KitIconHelper.createDefault(),
    destinationTourSceneGuid,
    originTourSceneGuid: position.virtualTourScene.sceneGuid as SceneGuid,
    position: LinkPositionHelper.toLinkPosition(position),
    destinationLabel,
    hasTilt: hasTilt ?? true,
  }),
  toSceneLinkHotspot: (link: SceneLink, onClick?: HandleSceneLinkClick): SceneLinkHotspot => {
    const sceneLinkParams: SceneLinkHotspotParameters = {
      position: LinkPositionHelper.toCoordinates(link.position, link.originTourSceneGuid),
      destinationTourSceneGuid: link.destinationTourSceneGuid,
      onClick: onClick,
      hasTilt: link.hasTilt,
    };
    const sceneLink = new SceneLinkHotspot(sceneLinkParams, VirtualTourIconsBundle, {
      sceneLinkDefaultBackgroundUrlTilted: backgroundUrlTilted,
      sceneLinkDefaultBackgroundUrl: backgroundUrl,
      sceneLinkDefaultImageUrl: imageUrl,
    });
    sceneLink.updateIcon(link.icon.kitIconId, link.hasTilt);

    if (link.destinationLabel) {
      sceneLink.updateDestinationLabel(link.destinationLabel);
    }
    return sceneLink;
  },
  toImageHotspot: (link: SceneLink): ImageHotspot => {
    const position = LinkPositionHelper.toCoordinates(link.position, link.originTourSceneGuid);
    const imageHotspot = new ImageHotspot(position, link.destinationLabel ?? null, link.hasTilt, VirtualTourIconsBundle, {
      sceneLinkDefaultBackgroundUrlTilted: backgroundUrlTilted,
      sceneLinkDefaultBackgroundUrl: backgroundUrl,
      sceneLinkDefaultImageUrl: imageUrl,
    });
    imageHotspot.updateIcon(link.icon.kitIconId, link.hasTilt);
    return imageHotspot;
  },
  updateDestinationSceneGuid: (link: SceneLink, sceneGuid: SceneGuid): SceneLink => ({
    ...link,
    destinationTourSceneGuid: sceneGuid,
  }),
  updateDestinationLabel: (link: SceneLink, label?: string): SceneLink => ({
    ...link,
    destinationLabel: label,
  }),
  updateCoordinates: (link: SceneLink, position: Position): SceneLink => ({
    ...link,
    originTourSceneGuid: position.virtualTourScene.sceneGuid as SceneGuid,
    position: LinkPositionHelper.toLinkPosition(position),
  }),
  updateIcon: (link: SceneLink, icon: KitIcon): SceneLink => ({
    ...link,
    icon,
  }),
  updateTilt: (link: SceneLink, hasTilt: boolean): SceneLink => ({
    ...link,
    hasTilt: hasTilt,
  }),
};
